import React, { useState, useRef, useEffect } from 'react';
import CodeBlock from './codeBlock';
import { toExportFormatArray } from '../utils/questionSchemaConverter';
import { notifySuccess } from '../services/notificationService';

/**
 * QuestionEditor - A reusable component for creating and editing quiz questions
 * 
 * @param {Object} props
 * @param {Array} props.questions - Array of existing questions
 * @param {Function} props.onQuestionsChange - Callback when questions are modified
 * @param {string} props.sessionId - Current session/quiz ID for export naming
 * @param {Function} props.onError - Callback to report errors
 * @param {number} props.initialEditIndex - Optional index of question to edit on load
 */
function QuestionEditor({ questions, onQuestionsChange, sessionId, onError, initialEditIndex }) {
  // Current question being edited
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    codeBlock: { language: 'javascript', code: '' },
    options: ['', '', '', ''],
    correctAnswerIndices: [0],
    allowMultiple: false,
    explanation: ''
  });
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [showCodeBlock, setShowCodeBlock] = useState(false);
  const [hasInitializedEdit, setHasInitializedEdit] = useState(false);
  
  // Import/Export
  const fileInputRef = useRef(null);
  const [importError, setImportError] = useState('');

  // Auto-edit question if initialEditIndex is provided
  useEffect(() => {
    if (initialEditIndex !== null && !hasInitializedEdit && questions.length > 0 && initialEditIndex < questions.length) {
      const q = questions[initialEditIndex];
      const correctIndices = q.correctAnswerIndices || [q.correctAnswerIndex || 0];
      setCurrentQuestion({
        ...q,
        correctAnswerIndices: correctIndices,
        allowMultiple: correctIndices.length > 1
      });
      setCurrentQuestionIndex(initialEditIndex);
      setShowCodeBlock(!!q.codeBlock?.code);
      setHasInitializedEdit(true);
      
      // Scroll to editor
      setTimeout(() => {
        document.getElementById('question-editor-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [initialEditIndex, questions, hasInitializedEdit]);

  // Handle question text change
  const handleQuestionChange = (e) => {
    setCurrentQuestion({ ...currentQuestion, question: e.target.value });
  };

  // Handle code block changes
  const handleCodeBlockChange = (e) => {
    setCurrentQuestion({
      ...currentQuestion,
      codeBlock: {
        ...currentQuestion.codeBlock,
        code: e.target.value
      }
    });
  };

  // Handle code language change
  const handleLanguageChange = (e) => {
    setCurrentQuestion({
      ...currentQuestion,
      codeBlock: {
        ...currentQuestion.codeBlock,
        language: e.target.value
      }
    });
  };

  // Handle option change
  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = value;
    setCurrentQuestion({ ...currentQuestion, options: newOptions });
  };

  // Handle correct answer change (supports both single and multiple)
  const handleCorrectAnswerChange = (index) => {
    if (currentQuestion.allowMultiple) {
      // Multiple selection mode - toggle the index
      const currentIndices = currentQuestion.correctAnswerIndices || [];
      if (currentIndices.includes(index)) {
        // Remove if already selected (but keep at least one)
        if (currentIndices.length > 1) {
          setCurrentQuestion({
            ...currentQuestion,
            correctAnswerIndices: currentIndices.filter(i => i !== index)
          });
        }
      } else {
        // Add to selection
        setCurrentQuestion({
          ...currentQuestion,
          correctAnswerIndices: [...currentIndices, index].sort((a, b) => a - b)
        });
      }
    } else {
      // Single selection mode
      setCurrentQuestion({ 
        ...currentQuestion, 
        correctAnswerIndices: [index],
        correctAnswerIndex: index // Keep for backward compatibility
      });
    }
  };

  // Toggle multiple answers mode
  const toggleMultipleAnswers = () => {
    const newAllowMultiple = !currentQuestion.allowMultiple;
    if (!newAllowMultiple) {
      // Switching to single mode - keep only first correct answer
      const firstCorrect = (currentQuestion.correctAnswerIndices || [0])[0] || 0;
      setCurrentQuestion({
        ...currentQuestion,
        allowMultiple: false,
        correctAnswerIndices: [firstCorrect],
        correctAnswerIndex: firstCorrect
      });
    } else {
      setCurrentQuestion({
        ...currentQuestion,
        allowMultiple: true
      });
    }
  };

  // Add new option
  const addOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      options: [...currentQuestion.options, '']
    });
  };

  // Remove option
  const removeOption = (index) => {
    if (currentQuestion.options.length <= 2) {
      onError?.("Eine Frage muss mindestens 2 Antwortmöglichkeiten haben");
      return;
    }
    
    const newOptions = [...currentQuestion.options];
    newOptions.splice(index, 1);
    
    // Adjust correctAnswerIndices for removed option
    let newCorrectIndices = (currentQuestion.correctAnswerIndices || [0]).map(i => {
      if (i === index) return -1; // Mark for removal
      if (i > index) return i - 1; // Decrement if after removed
      return i;
    }).filter(i => i >= 0);
    
    // Ensure at least one correct answer
    if (newCorrectIndices.length === 0) {
      newCorrectIndices = [0];
    }
    
    setCurrentQuestion({
      ...currentQuestion,
      options: newOptions,
      correctAnswerIndices: newCorrectIndices,
      correctAnswerIndex: newCorrectIndices[0]
    });
  };

  // Save current question
  const saveQuestion = () => {
    // Validate question
    if (!currentQuestion.question.trim()) {
      onError?.("Bitte gib eine Frage ein");
      return;
    }
    
    if (currentQuestion.options.some(opt => !opt.trim())) {
      onError?.("Alle Antwortoptionen müssen ausgefüllt sein");
      return;
    }
    
    // Prepare question data with both old and new format for compatibility
    const questionToSave = {
      ...currentQuestion,
      correctAnswerIndex: (currentQuestion.correctAnswerIndices || [0])[0],
      correctAnswerIndices: currentQuestion.correctAnswerIndices || [currentQuestion.correctAnswerIndex || 0]
    };
    
    const newQuestions = [...questions];
    
    if (currentQuestionIndex === -1) {
      // Add new question
      newQuestions.push(questionToSave);
    } else {
      // Update existing question
      newQuestions[currentQuestionIndex] = questionToSave;
    }
    
    // Notify parent of changes
    onQuestionsChange(newQuestions);
    
    // Reset form
    resetForm();
  };

  // Reset the form to initial state
  const resetForm = () => {
    setCurrentQuestion({
      question: '',
      codeBlock: { language: 'javascript', code: '' },
      options: ['', '', '', ''],
      correctAnswerIndices: [0],
      correctAnswerIndex: 0,
      allowMultiple: false,
      explanation: ''
    });
    setCurrentQuestionIndex(-1);
    setShowCodeBlock(false);
  };

  // Edit existing question
  const editQuestion = (index) => {
    const q = questions[index];
    // Migrate old format to new format if needed
    const correctIndices = q.correctAnswerIndices || [q.correctAnswerIndex || 0];
    setCurrentQuestion({
      ...q,
      correctAnswerIndices: correctIndices,
      allowMultiple: correctIndices.length > 1
    });
    setCurrentQuestionIndex(index);
    setShowCodeBlock(!!q.codeBlock?.code);
  };

  // Delete question
  const deleteQuestion = (index) => {
    if (window.confirm("Möchtest du diese Frage wirklich löschen?")) {
      const newQuestions = [...questions];
      newQuestions.splice(index, 1);
      
      onQuestionsChange(newQuestions);
      
      if (currentQuestionIndex === index) {
        // Reset form if deleting the currently edited question
        resetForm();
      } else if (currentQuestionIndex > index) {
        // Adjust currentQuestionIndex if necessary
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      }
    }
  };

  // Export questions to JSON
  const exportQuestions = () => {
    if (questions.length === 0) {
      onError?.("Keine Fragen zum Exportieren vorhanden");
      return;
    }
    
    const exportData = toExportFormatArray(questions);
    const fileName = `quiz-fragen-${sessionId || 'export'}.json`;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
    
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", fileName);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  // Import questions from JSON file
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/json') {
      setImportError('Bitte nur JSON-Dateien hochladen');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedQuestions = JSON.parse(e.target.result);
        
        if (!Array.isArray(importedQuestions)) {
          setImportError('Ungültiges Format: Die Datei muss ein Array von Fragen enthalten');
          return;
        }
        
        // Validate each question - supports both single (correctAnswerIndex) and multiple (correctAnswerIndices) answer formats
        const validateQuestion = (q) => {
          if (!q.question || !Array.isArray(q.options) || q.options.length < 2) {
            return false;
          }
          
          const hasSingleAnswer = typeof q.correctAnswerIndex === 'number';
          const hasMultipleAnswers = Array.isArray(q.correctAnswerIndices) && q.correctAnswerIndices.length > 0;
          
          // Must have either correctAnswerIndex OR correctAnswerIndices
          if (!hasSingleAnswer && !hasMultipleAnswers) {
            return false;
          }
          
          // Validate that indices are within valid range
          if (hasSingleAnswer && (q.correctAnswerIndex < 0 || q.correctAnswerIndex >= q.options.length)) {
            return false;
          }
          
          if (hasMultipleAnswers) {
            const allIndicesValid = q.correctAnswerIndices.every(
              idx => typeof idx === 'number' && idx >= 0 && idx < q.options.length
            );
            if (!allIndicesValid) {
              return false;
            }
          }
          
          return true;
        };
        
        const isValid = importedQuestions.every(validateQuestion);
        
        if (!isValid) {
          setImportError('Ungültiges Format: Jede Frage muss question, options und correctAnswerIndex oder correctAnswerIndices enthalten. Indizes müssen gültig sein.');
          return;
        }
        
        onQuestionsChange(importedQuestions);
        setImportError('');
        notifySuccess('Quiz-Fragen erfolgreich importiert!');
      } catch (error) {
        setImportError('Fehler beim Lesen der Datei: ' + error.message);
      }
    };
    reader.readAsText(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {/* Question Editor Form */}
      <div id="question-editor-form" className="mb-8 p-4 bg-gray-50 rounded border">
        <h3 className="font-medium mb-3">
          {currentQuestionIndex === -1 ? "Neue Frage erstellen" : `Frage ${currentQuestionIndex + 1} bearbeiten`}
        </h3>
        
        {/* Question Text */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Frage</label>
          <textarea
            value={currentQuestion.question}
            onChange={handleQuestionChange}
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="Gib hier deine Frage ein..."
          />
        </div>
        
        {/* Code Block Section */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <label className="block text-sm font-medium">Code-Block</label>
            <button
              type="button"
              onClick={() => setShowCodeBlock(!showCodeBlock)}
              className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded"
            >
              {showCodeBlock ? "Ausblenden" : "Einblenden"}
            </button>
          </div>
          
          {showCodeBlock && (
            <div className="space-y-2">
              <select
                value={currentQuestion.codeBlock.language}
                onChange={handleLanguageChange}
                className="w-full p-2 border rounded"
              >
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="csharp">C#</option>
                <option value="cpp">C++</option>
                <option value="bash">Bash</option>
                <option value="sql">SQL</option>
              </select>
              
              <textarea
                value={currentQuestion.codeBlock.code}
                onChange={handleCodeBlockChange}
                className="w-full p-2 border rounded font-mono"
                rows={5}
                placeholder="Code hier eingeben..."
              />
              
              {currentQuestion.codeBlock.code && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium mb-1">Vorschau:</h4>
                  <CodeBlock
                    language={currentQuestion.codeBlock.language}
                    code={currentQuestion.codeBlock.code}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Answer Options Section */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <label className="block text-sm font-medium">Antwortmöglichkeiten</label>
              <button
                type="button"
                onClick={toggleMultipleAnswers}
                className={`ml-3 text-xs px-2 py-1 rounded ${
                  currentQuestion.allowMultiple 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}
                title={currentQuestion.allowMultiple ? "Mehrere richtige Antworten aktiviert" : "Nur eine richtige Antwort"}
              >
                {currentQuestion.allowMultiple ? '☑ Mehrfachauswahl' : '○ Einzelauswahl'}
              </button>
            </div>
            <button
              type="button"
              onClick={addOption}
              className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
            >
              + Option hinzufügen
            </button>
          </div>
          
          {currentQuestion.allowMultiple && (
            <div className="text-xs text-purple-600 mb-2">
              ✓ Mehrere richtige Antworten möglich - Klicke auf die Checkboxen um mehrere auszuwählen
            </div>
          )}
          
          {currentQuestion.options.map((option, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type={currentQuestion.allowMultiple ? "checkbox" : "radio"}
                name="correctAnswer"
                checked={(currentQuestion.correctAnswerIndices || []).includes(index)}
                onChange={() => handleCorrectAnswerChange(index)}
                className="mr-2"
              />
              <input
                type="text"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                className={`flex-1 p-2 border rounded ${
                  (currentQuestion.correctAnswerIndices || []).includes(index) 
                    ? 'border-green-400 bg-green-50' 
                    : ''
                }`}
              />
              {currentQuestion.options.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  className="ml-2 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          
          <div className="text-xs text-gray-500 mt-1">
            Wähle die richtige Antwort durch Auswahl des Radiobuttons aus
          </div>
        </div>
        
        {/* Explanation Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Erklärung <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Diese Erklärung wird nach Beantwortung der Frage angezeigt
          </p>
          <textarea
            value={currentQuestion.explanation || ''}
            onChange={(e) => setCurrentQuestion({ ...currentQuestion, explanation: e.target.value })}
            placeholder="Erkläre hier, warum diese Antwort richtig ist..."
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end space-x-2">
          {currentQuestionIndex !== -1 && (
            <button
              onClick={resetForm}
              className="bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300"
            >
              Abbrechen
            </button>
          )}
          <button
            onClick={saveQuestion}
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
          >
            {currentQuestionIndex === -1 ? "Frage hinzufügen" : "Frage aktualisieren"}
          </button>
        </div>
      </div>
      
      {/* Question List */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">Fragen ({questions.length})</h3>
          <div className="space-x-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".json"
              className="hidden"
              id="import-file"
            />
            <label
              htmlFor="import-file"
              className="cursor-pointer bg-indigo-50 text-indigo-700 py-1 px-3 rounded text-sm hover:bg-indigo-100"
            >
              Fragen importieren
            </label>
            <button
              onClick={exportQuestions}
              disabled={questions.length === 0}
              className={`py-1 px-3 rounded text-sm ${
                questions.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              }`}
            >
              Fragen exportieren
            </button>
          </div>
        </div>
        
        {importError && (
          <div className="text-red-600 text-sm mb-2">{importError}</div>
        )}
        
        {questions.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 border rounded">
            <p className="text-gray-500">Noch keine Fragen vorhanden</p>
            <p className="text-sm text-gray-400 mt-1">Erstelle eine neue Frage oder importiere Fragen</p>
          </div>
        ) : (
          <div className="bg-gray-50 border rounded max-h-96 overflow-y-auto">
            {questions.map((q, index) => (
              <div key={index} className="p-3 border-b last:border-b-0">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="font-medium mb-1">
                      Frage {index + 1}: {q.question}
                    </div>
                    
                    {q.codeBlock && q.codeBlock.code && (
                      <div className="text-xs text-gray-500 mb-1">
                        Enthält Code ({q.codeBlock.language})
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Antworten:</span>
                      {(q.correctAnswerIndices || [q.correctAnswerIndex]).length > 1 && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1 rounded">Mehrfachauswahl</span>
                      )}
                      <ol className="list-decimal list-inside ml-1">
                        {q.options.map((opt, i) => {
                          const correctIndices = q.correctAnswerIndices || [q.correctAnswerIndex];
                          const isCorrect = correctIndices.includes(i);
                          return (
                            <li key={i} className={isCorrect ? "text-green-600 font-medium" : ""}>
                              {opt} {isCorrect && " ✓"}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  </div>
                  
                  <div className="flex ml-4">
                    <button
                      onClick={() => editQuestion(index)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => deleteQuestion(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default QuestionEditor;
